<template>
  <v-main>
    <v-container fluid class="privacy-policy-container">
      <v-card elevation="3" rounded="lg" class="mx-auto" max-width="1200">
        <v-card-title class="text-h4 font-weight-bold text-center primary white--text py-6">
          Privacy Policy
        </v-card-title>

        <v-card-text class="pa-8">
          <div class="document-content">

            <!-- BACKGROUND LOCATION TRACKING -->
            <section class="mb-8">
              <h2 class="section-title text-h5 font-weight-bold mb-4 primary--text">
                BACKGROUND LOCATION TRACKING
              </h2>
              <p class="body-1 mb-3">
                Walkies collects precise location data to enable real-time pet walk tracking,
                <strong>even when the app is closed or not in use.</strong>
              </p>

              <h3 class="subsection-title text-h6 font-weight-bold mt-5 mb-3">
                How It Works
              </h3>
              <ul class="body-1 mb-4">
                <li>Location tracking ONLY activates during active pet walks</li>
                <li>Allows pet owners to monitor their pet's walk route in real-time</li>
                <li>Ensures pet safety by providing live location updates</li>
                <li>Tracking automatically stops when the walk is marked as complete</li>
                <li>No location tracking occurs when you're not on an active walk</li>
              </ul>

              <h3 class="subsection-title text-h6 font-weight-bold mt-5 mb-3">
                Why We Need This Permission
              </h3>
              <ul class="body-1 mb-4">
                <li>To show pet owners exactly where their pet is during walks</li>
                <li>To provide route mapping and distance tracking</li>
                <li>To ensure accountability and safety of pet care services</li>
                <li>To generate walk reports and history</li>
              </ul>

              <h3 class="subsection-title text-h6 font-weight-bold mt-5 mb-3">
                Your Control
              </h3>
              <ul class="body-1 mb-4">
                <li>You can disable location permissions in your device settings at any time</li>
                <li>Disabling location will prevent the walk tracking feature from working</li>
                <li>Location data is used exclusively for walk tracking services</li>
                <li>Your location data is never sold to third parties</li>
              </ul>
            </section>

            <v-divider class="my-6"></v-divider>

            <!-- INFORMATION WE COLLECT -->
            <section class="mb-8">
              <h2 class="section-title text-h5 font-weight-bold mb-4 primary--text">
                INFORMATION WE COLLECT
              </h2>

              <div class="subsection mb-5">
                <h3 class="subsection-title text-h6 font-weight-bold mb-3">
                  1. Location Data
                </h3>
                <ul class="body-1">
                  <li><strong>Type:</strong> Precise GPS location (latitude, longitude)</li>
                  <li><strong>When:</strong> During active pet walks only</li>
                  <li><strong>Purpose:</strong> Real-time walk tracking and route mapping</li>
                  <li><strong>Background Access:</strong> Yes (only during active walks)</li>
                  <li><strong>Retention:</strong> Walk routes stored for 2 years for record-keeping</li>
                </ul>
              </div>

              <div class="subsection mb-5">
                <h3 class="subsection-title text-h6 font-weight-bold mb-3">
                  2. Personal Information
                </h3>
                <ul class="body-1">
                  <li>Name (first and last name)</li>
                  <li>Email address</li>
                  <li>Phone number</li>
                  <li>Password (encrypted)</li>
                  <li><strong>Purpose:</strong> Account creation, authentication, and service communication</li>
                  <li><strong>Retention:</strong> While account is active, deleted within 30 days of account deletion</li>
                </ul>
              </div>

              <div class="subsection mb-5">
                <h3 class="subsection-title text-h6 font-weight-bold mb-3">
                  3. Pet Information
                </h3>
                <ul class="body-1">
                  <li>Pet names, breeds, ages, descriptions</li>
                  <li>Pet photos</li>
                  <li>Special care instructions</li>
                  <li><strong>Purpose:</strong> Service delivery and pet identification</li>
                  <li><strong>Retention:</strong> While account is active</li>
                </ul>
              </div>

              <div class="subsection mb-5">
                <h3 class="subsection-title text-h6 font-weight-bold mb-3">
                  4. Photos and Media
                </h3>
                <ul class="body-1">
                  <li><strong>Camera Access:</strong> To capture pet photos and walk moments</li>
                  <li><strong>Photo Storage:</strong> Stored securely on our servers</li>
                  <li><strong>Usage:</strong> Pet profiles and walk updates</li>
                  <li><strong>Purpose:</strong> Service delivery and communication with pet owners</li>
                </ul>
              </div>

              <div class="subsection mb-5">
                <h3 class="subsection-title text-h6 font-weight-bold mb-3">
                  5. App Activity and Usage
                </h3>
                <ul class="body-1">
                  <li>Walk history and duration</li>
                  <li>Booking records</li>
                  <li>Service preferences</li>
                  <li>Device information (model, OS version, app version)</li>
                  <li><strong>Purpose:</strong> Service delivery, app improvement, and support</li>
                </ul>
              </div>
            </section>

            <v-divider class="my-6"></v-divider>

            <!-- HOW WE USE YOUR INFORMATION -->
            <section class="mb-8">
              <h2 class="section-title text-h5 font-weight-bold mb-4 primary--text">
                HOW WE USE YOUR INFORMATION
              </h2>
              <ul class="body-1 mb-4">
                <li>Provide pet walking and care services</li>
                <li>Track pet walks in real-time with GPS</li>
                <li>Connect you with verified pet care providers</li>
                <li>Send walk updates, photos, and notifications</li>
                <li>Process payments and manage bookings</li>
                <li>Improve services and app functionality</li>
                <li>Provide customer support</li>
                <li>Ensure safety and prevent fraud</li>
                <li>Comply with legal obligations</li>
              </ul>

              <h3 class="subsection-title text-h6 font-weight-bold mt-5 mb-3">
                We Do NOT
              </h3>
              <ul class="body-1">
                <li>Sell your personal data to third parties</li>
                <li>Share your location data with advertisers</li>
                <li>Use your data for purposes unrelated to pet care services</li>
              </ul>
            </section>

            <v-divider class="my-6"></v-divider>

            <!-- WHO WE SHARE YOUR DATA WITH -->
            <section class="mb-8">
              <h2 class="section-title text-h5 font-weight-bold mb-4 primary--text">
                WHO WE SHARE YOUR DATA WITH
              </h2>

              <div class="subsection mb-5">
                <h3 class="subsection-title text-h6 font-weight-bold mb-3">
                  1. Pet Care Providers
                </h3>
                <p class="body-1">
                  Necessary information (pet details, walk location) shared only to deliver booked services
                </p>
              </div>

              <div class="subsection mb-5">
                <h3 class="subsection-title text-h6 font-weight-bold mb-3">
                  2. Service Providers
                </h3>
                <ul class="body-1">
                  <li>Cloud hosting (Firebase / Google Cloud)</li>
                  <li>Payment processors</li>
                  <li>Analytics services</li>
                  <li>All providers are contractually bound to protect your data</li>
                </ul>
              </div>

              <div class="subsection mb-5">
                <h3 class="subsection-title text-h6 font-weight-bold mb-3">
                  3. Legal Requirements
                </h3>
                <p class="body-1">
                  Data may be disclosed if required by law
                </p>
              </div>

              <h3 class="subsection-title text-h6 font-weight-bold mt-5 mb-3">
                We Do NOT
              </h3>
              <ul class="body-1">
                <li>Sell or rent your data</li>
                <li>Share location data with advertisers</li>
                <li>Allow third-party marketing use</li>
              </ul>
            </section>

            <v-divider class="my-6"></v-divider>

            <!-- YOUR PRIVACY RIGHTS -->
            <section class="mb-8">
              <h2 class="section-title text-h5 font-weight-bold mb-4 primary--text">
                YOUR PRIVACY RIGHTS
              </h2>

              <div class="subsection mb-5">
                <h3 class="subsection-title text-h6 font-weight-bold mb-3">
                  Access & Update Information
                </h3>
                <ul class="body-1">
                  <li>Edit profile in App Settings</li>
                  <li>Update pet information anytime</li>
                  <li>Change contact preferences</li>
                </ul>
              </div>

              <div class="subsection mb-5">
                <h3 class="subsection-title text-h6 font-weight-bold mb-3">
                  Delete Your Data
                </h3>
                <ul class="body-1">
                  <li>Settings → Account → Delete Account</li>
                  <li>Or email: <a href="mailto:support@petwalkie.com">support@petwalkie.com</a></li>
                  <li>Data deleted within 30 days</li>
                  <li>Walk history retained for 2 years</li>
                </ul>
              </div>

              <div class="subsection mb-5">
                <h3 class="subsection-title text-h6 font-weight-bold mb-3">
                  Control Location Access
                </h3>
                <p class="body-1 font-weight-bold">Android:</p>
                <ul class="body-1">
                  <li>Settings → Apps → Walkies → Permissions → Location</li>
                  <li>Choose: Allow while using app or Deny</li>
                  <li><strong>Note:</strong> Denying disables walk tracking</li>
                </ul>
              </div>

              <div class="subsection mb-5">
                <h3 class="subsection-title text-h6 font-weight-bold mb-3">
                  Control Notifications
                </h3>
                <ul class="body-1">
                  <li>App Settings → Notifications</li>
                  <li>Or device notification settings</li>
                </ul>
              </div>

              <div class="subsection mb-5">
                <h3 class="subsection-title text-h6 font-weight-bold mb-3">
                  Control Camera Access
                </h3>
                <p class="body-1">
                  Settings → Apps → Walkies → Permissions → Camera
                </p>
              </div>

              <div class="subsection mb-5">
                <h3 class="subsection-title text-h6 font-weight-bold mb-3">
                  Request Data Copy
                </h3>
                <ul class="body-1">
                  <li>Email: <a href="mailto:support@petwalkie.com">support@petwalkie.com</a></li>
                  <li>Provided within 30 days</li>
                </ul>
              </div>
            </section>

            <v-divider class="my-6"></v-divider>

            <!-- CHILDREN'S PRIVACY -->
            <section class="mb-8">
              <h2 class="section-title text-h5 font-weight-bold mb-4 primary--text">
                CHILDREN'S PRIVACY
              </h2>
              <ul class="body-1">
                <li>Walkies is not intended for children under 13</li>
                <li>No intentional data collection from children</li>
                <li>Data deleted immediately if discovered</li>
                <li>Contact us if you believe a child provided data</li>
              </ul>
            </section>

            <v-divider class="my-6"></v-divider>

            <!-- DATA SECURITY -->
            <section class="mb-8">
              <h2 class="section-title text-h5 font-weight-bold mb-4 primary--text">
                DATA SECURITY
              </h2>
              <ul class="body-1">
                <li>SSL/TLS encrypted transmission</li>
                <li>Encrypted password storage</li>
                <li>Secure Firebase / Google Cloud infrastructure</li>
                <li>Regular security audits</li>
                <li>Access control and authentication</li>
                <li>PCI-DSS compliant payments</li>
              </ul>
            </section>

            <v-divider class="my-6"></v-divider>

            <!-- DATA RETENTION -->
            <section class="mb-8">
              <h2 class="section-title text-h5 font-weight-bold mb-4 primary--text">
                DATA RETENTION
              </h2>
              <ul class="body-1">
                <li><strong>Active accounts:</strong> While account is active</li>
                <li><strong>Walk history:</strong> 2 years</li>
                <li><strong>Deleted accounts:</strong> Removed within 30 days</li>
                <li><strong>Legal retention:</strong> As required</li>
                <li><strong>Anonymized analytics data:</strong> Retained indefinitely</li>
              </ul>
            </section>

            <v-divider class="my-6"></v-divider>

            <!-- THIRD-PARTY SERVICES -->
            <section class="mb-8">
              <h2 class="section-title text-h5 font-weight-bold mb-4 primary--text">
                THIRD-PARTY SERVICES
              </h2>
              <p class="body-1 font-weight-bold mb-2">We use the following third-party services:</p>
              <ul class="body-1 mb-4">
                <li>Firebase (Google)</li>
                <li>Google Maps</li>
                <li>Google Sign-In</li>
                <li>Payment processors</li>
              </ul>

              <p class="body-1 font-weight-bold mb-2">Privacy Policies:</p>
              <ul class="body-1">
                <li>Google: <a href="https://policies.google.com/privacy" target="_blank">https://policies.google.com/privacy</a></li>
                <li>Firebase: <a href="https://firebase.google.com/support/privacy" target="_blank">https://firebase.google.com/support/privacy</a></li>
              </ul>
            </section>

            <v-divider class="my-6"></v-divider>

            <!-- CONTACT US -->
            <section class="mb-8">
              <h2 class="section-title text-h5 font-weight-bold mb-4 primary--text">
                CONTACT US
              </h2>
              <ul class="body-1">
                <li><strong>Email:</strong> <a href="mailto:support@petwalkie.com">support@petwalkie.com</a></li>
                <li><strong>Company:</strong> Walkies Pet Care Services</li>
                <li><strong>Response Time:</strong> Within 48 hours</li>
              </ul>
            </section>

            <v-divider class="my-6"></v-divider>

            <!-- CHANGES TO THIS PRIVACY POLICY -->
            <section class="mb-8">
              <h2 class="section-title text-h5 font-weight-bold mb-4 primary--text">
                CHANGES TO THIS PRIVACY POLICY
              </h2>
              <ul class="body-1">
                <li>Updates may occur due to legal or feature changes</li>
                <li>Notifications via app, email, or policy update date</li>
              </ul>
            </section>

            <v-divider class="my-6"></v-divider>

            <!-- INTERNATIONAL DATA TRANSFERS -->
            <section class="mb-8">
              <h2 class="section-title text-h5 font-weight-bold mb-4 primary--text">
                INTERNATIONAL DATA TRANSFERS
              </h2>
              <ul class="body-1">
                <li>Data may be stored in other countries</li>
                <li>Appropriate safeguards are applied</li>
                <li>Continued use implies consent</li>
              </ul>
            </section>

            <v-divider class="my-6"></v-divider>

            <!-- FOOTER NOTE -->
            <section class="mb-4">
              <v-alert type="info" outlined class="mb-0">
                <p class="body-2 mb-0">
                  <strong>Last Updated:</strong> {{ new Date().toLocaleDateString() }}
                </p>
              </v-alert>
            </section>

          </div>
        </v-card-text>
      </v-card>
    </v-container>
  </v-main>
</template>

<script>
export default {
  name: "PrivacyPolicyPage",
  data() {
    return {};
  },
};
</script>

<style scoped>
.privacy-policy-container {
  background-color: #f5f5f5;
  min-height: 100vh;
  padding: 40px 20px;
}

.document-content {
  line-height: 1.8;
}

.section-title {
  border-bottom: 2px solid #1976d2;
  padding-bottom: 8px;
}

.subsection-title {
  color: #424242;
}

.subsection {
  margin-left: 16px;
}

ul {
  list-style-type: disc;
  margin-left: 24px;
}

li {
  margin-bottom: 8px;
}

a {
  color: #1976d2;
  text-decoration: none;
}

a:hover {
  text-decoration: underline;
}

@media print {
  .privacy-policy-container {
    background-color: white;
  }
}
</style>
